import {
	Anchor,
	Box,
	createStyles,
	Divider,
	Group,
	Stack,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
	useCreatePersona,
	useDeletePersona,
	useGetAiAccessStatus,
	useGetPersonas,
	useWorkspaceAiFeedback,
} from '@repo/api-codegen';
import {
	SettingsBlock,
	SettingsButton,
	SettingsSwitch,
} from '@repo/common/components';
import { AIAvatar } from '@repo/common/components/AI/Avatar';
import MarkdownRenderer from '@repo/common/components/MarkdownRenderer';
import {
	getToolDescription,
	getToolName,
} from '@repo/common/constants/aiTools';
import { Banner, Button, Text, TextArea, Title } from '@repo/foundations';
import dayjs from 'dayjs';
import { capitalize, toLower } from 'lodash-es';
import { useCallback, useState } from 'react';
import {
	useAgentTools,
	useAuthUser,
	useDisableAgentTool,
	useEnableAgentTool,
	useGetAgentInstructions,
	useGetCustomDescriptionStyle,
	useUpdateAgentInstructions,
	useUpdateCustomDescriptionStyle,
} from '../../api';
import { useUpdateWorkspace } from '../../api/hooks/workspace/useUpdateWorkspace';
import type { IAgentTool } from '../../api/types';
import { useAiWorkspaceSettingEnabled } from '../../hooks/useAiWorkspaceSettingEnabled';
import { usePlan } from '../../hooks/usePlans';
import { useFeatureFlags } from '../../utils/featureFlags';
import { sentenceCase } from '../../utils/stringUtils';
import { closeAllModals, openModal } from '../ModalManager';
import { AIPersonaCard } from './AIPersonas/AIPersonaCard';
import { LLMProviderSettings } from './LLMProviderSettings';

const ID_CUSTOM = 'custom-instructions-modal';

function ToolsSettings() {
	const { data: tools } = useAgentTools();
	const { mutateAsync: enableAgentTool } = useEnableAgentTool();
	const { mutateAsync: disableAgentTool } = useDisableAgentTool();

	const docsTools = {
		toolNames: ['get_secoda_docs'],
		tools: [] as IAgentTool[],
		uiName: 'get_secoda_docs',
	};
	const sqlTools = {
		toolNames: ['run_sql'],
		tools: [] as IAgentTool[],
		uiName: 'run_sql',
	};

	tools?.tools.map((tool) => {
		if (docsTools.toolNames.includes(tool.name)) {
			docsTools.tools.push(tool);
		}

		if (sqlTools.toolNames.includes(tool.name)) {
			sqlTools.tools.push(tool);
		}
	});

	const renderTool = (config: {
		tools: IAgentTool[];
		uiName: string;
		toolNames: string[];
	}) => {
		const tool = config.tools.find((t) => config.toolNames.includes(t.name));
		if (!tool) return null;

		return (
			<SettingsSwitch
				disabled={false}
				key={tool.name}
				title={getToolName(
					tool.name,
					capitalize(toLower(sentenceCase(tool.name)))
				)}
				checked={tool.enabled}
				description={getToolDescription(tool.name, tool.frontend_description)}
				onChange={() => {
					const action = !tool.enabled ? enableAgentTool : disableAgentTool;
					// Toggle UI first to avoid delay
					action({ tool });
					config.tools.forEach((toggle) => {
						if (toggle.name !== tool.name) {
							action({ tool: toggle });
						}
					});
				}}
			/>
		);
	};

	return <>{[sqlTools, docsTools].map((config) => renderTool(config))}</>;
}

const useStyles = createStyles((theme) => ({
	textBox: {
		'& textarea': {
			resize: 'vertical',
		},
	},
	saveButton: {
		marginTop: theme.spacing.md,
		marginBottom: theme.spacing.md,
		display: 'block',
		marginLeft: 'auto',
	},
	anchor: {
		textDecoration: 'underline',
		color: 'inherit',
	},
}));

function CustomInstructionsModal({
	remoteInstructions,
	remoteDescriptionStyle,
}: {
	remoteInstructions?: string;
	remoteDescriptionStyle?: string;
}) {
	const { mutateAsync: updateAgentInstructions } = useUpdateAgentInstructions();
	const { mutateAsync: updateCustomDescriptionStyle } =
		useUpdateCustomDescriptionStyle();

	const [instructions, setInstructions] = useState<string>(
		remoteInstructions ?? ''
	);
	const [descriptionStyle, setDescriptionStyle] = useState<string>(
		remoteDescriptionStyle ?? ''
	);

	const { classes } = useStyles();

	const notifySuccessOnSave = useCallback((type: string) => {
		showNotification({
			title: `${type} saved`,
			message: '',
			autoClose: 1500,
		});
	}, []);

	const notifyErrorOnSave = useCallback(() => {
		showNotification({
			title: 'Something went wrong',
			message: 'Please enter the instructions again',
			autoClose: 1500,
		});
	}, []);

	const handleSave = async () => {
		try {
			await updateCustomDescriptionStyle({ instructions: descriptionStyle }),
				await updateAgentInstructions({ instructions }),
				notifySuccessOnSave('Secoda AI custom instructions');
			closeAllModals();
		} catch {
			notifyErrorOnSave();
		}
	};

	return (
		<>
			<Title size="sm" weight="semibold">
				Custom chat instructions{' '}
				<Text size="sm" span color="text/secondary/default">
					(Optional)
				</Text>
			</Title>
			<TextArea
				minRows={3}
				className={classes.textBox}
				value={instructions}
				onChange={(e) => setInstructions(e.target.value)}
			/>
			<Text size="sm" mb="sm" color="text/secondary/default">
				Provide custom instructions to Secoda AI. These instructions will be
				injected into the prompt.
			</Text>
			<Title size="sm" weight="semibold">
				Custom description instructions{' '}
				<Text size="sm" span color="text/secondary/default">
					(Optional)
				</Text>
			</Title>
			<TextArea
				minRows={3}
				className={classes.textBox}
				value={descriptionStyle}
				onChange={(e) => setDescriptionStyle(e.target.value)}
			/>
			<Text size="sm" mb="sm" color="text/secondary/default">
				Custom instructions for Secoda AI when generating descriptions for your
				resources.
			</Text>
			<Divider mb="lg" />
			<Group align="center" position="right" spacing="xs">
				<Button
					variant="default"
					onClick={() => {
						closeAllModals();
					}}
				>
					Cancel
				</Button>
				<Button variant="primary" onClick={handleSave}>
					Save
				</Button>
			</Group>
		</>
	);
}

export function AIToolsSettings() {
	const { workspace, isAdminUser } = useAuthUser();
	const { aiPersonas, executeAccessV2, customLlmProvider } = useFeatureFlags();
	const { mutateAsync: updateWorkspace } = useUpdateWorkspace(workspace.id);
	const { data: aiFeedback } = useWorkspaceAiFeedback({});
	const { data: remoteInstructions, isLoading: isLoadingInstructions } =
		useGetAgentInstructions();
	const { data: remoteDescriptionStyle, isLoading: isLoadingDescriptionStyle } =
		useGetCustomDescriptionStyle();
	const enableAi = useAiWorkspaceSettingEnabled();

	const { classes } = useStyles();

	const showCustomInstructionsModal = () => {
		openModal({
			title: 'Custom instructions',
			id: ID_CUSTOM,
			children: (
				<CustomInstructionsModal
					remoteInstructions={remoteInstructions}
					remoteDescriptionStyle={remoteDescriptionStyle}
				/>
			),
		});
	};

	const { data: personas, refetch } = useGetPersonas(
		{
			queryParams: {
				orderBy: 'created_at',
				order: 'desc',
			},
		},
		{
			select: (data) =>
				data.sort((a, b) => dayjs(b.created_at).diff(dayjs(a.created_at))),
		}
	);

	const { mutateAsync: createPersona } = useCreatePersona();
	const { mutateAsync: deletePersona } = useDeletePersona();

	const handleCreatePersona = useCallback(async () => {
		await createPersona({});
		await refetch();
	}, [createPersona, refetch]);

	const handleDeletePersona = useCallback(
		async (id: string) => {
			await deletePersona({
				pathParams: {
					personaId: id || '',
				},
			});
			await refetch();
		},
		[deletePersona, refetch]
	);

	const { data, refetch: refetchAiAccessStatus } = useGetAiAccessStatus({});
	const plan = usePlan();

	const isLitePlan = plan.plan?.id === 'SECODA LITE';
	const workspaceAiSwitchDisabled = isLitePlan || !isAdminUser;

	return (
		<Stack spacing={0}>
			<Box mb="4xl">
				<SettingsSwitch
					// Secoda Lite always has access to Secoda AI; workspace ai_search is admin-only
					disabled={workspaceAiSwitchDisabled}
					fixedTooltipProps={
						!isAdminUser && !isLitePlan
							? {
									label:
										'Only workspace admins can enable or disable Secoda AI for the workspace.',
								}
							: undefined
					}
					leftComponent={<AIAvatar size="sm" speed="slow" />}
					onChange={async (checked) => {
						await updateWorkspace({
							data: { id: workspace.id, ai_search: checked },
						});
						await refetchAiAccessStatus();
					}}
					checked={
						data?.restriction_info.restriction_type !==
						'workspace_settings_disabled'
					}
					title="Secoda AI"
					description={
						<>
							By using Secoda AI you agree to the{' '}
							<Anchor
								href="https://docs.secoda.com/policies/secoda-ai-terms"
								target="_blank"
								className={classes.anchor}
							>
								Secoda AI Terms
							</Anchor>{' '}
							and our usage of{' '}
							<Anchor
								href="https://docs.secoda.com/features/ai-assistant/security"
								target="_blank"
								className={classes.anchor}
							>
								Anthropic
							</Anchor>{' '}
							and{' '}
							<Anchor
								href="https://docs.secoda.com/features/ai-assistant/security"
								target="_blank"
								className={classes.anchor}
							>
								Google
							</Anchor>
						</>
					}
				/>
			</Box>

			{enableAi && (
				<Box>
					<Title size="md" mb="md">
						Tools
					</Title>
					{executeAccessV2 && (
						<Box mb="md">
							<Banner
								tone="info"
								title="Fine-grained SQL query permissions"
								message="To manage who can run SQL with Secoda AI, configure the Query Permissions preference in your integration settings."
							/>
						</Box>
					)}
					<Box mb="4xl">
						<ToolsSettings />
					</Box>
					<Title size="md" mb="md">
						Customize
					</Title>
					<Box mb="4xl">
						<SettingsButton
							title="Custom instructions"
							description="Add custom instructions for chat and descriptions. These settings are applied to all users"
							buttonText="Manage instructions"
							onClick={showCustomInstructionsModal}
							disabled={isLoadingInstructions || isLoadingDescriptionStyle}
						/>
					</Box>

					{customLlmProvider && (
						<>
							<Title size="md" mb="md">
								LLM Providers
							</Title>
							<Box mb="4xl">
								<LLMProviderSettings />
							</Box>
						</>
					)}

					{aiPersonas && (
						<>
							<Title size="md" mb="md">
								Personas
							</Title>
							<Box mb="4xl">
								<SettingsButton
									iconName="plus"
									variant="tertiary"
									title={`${personas?.length || 0} ${
										personas?.length === 1 ? 'persona' : 'personas'
									}`}
									onClick={handleCreatePersona}
									aria-label="Create persona"
									tooltip="Create persona"
								/>
								{personas?.map((persona) => (
									<AIPersonaCard
										key={persona.id}
										id={persona.id}
										name={persona.name}
										description={persona.description}
										avatarURL={persona.avatar_url}
										customInstructions={persona.custom_instructions}
										onDelete={handleDeletePersona}
										teams={persona.teams}
										groups={persona.groups}
										isDefaultPersona={persona.is_default}
										maskPiiOnResponseGeneration={
											persona.mask_pii_on_response_generation
										}
									/>
								))}
							</Box>
						</>
					)}
					<Box mt="xl">
						{aiFeedback && (
							<SettingsBlock
								title="Suggestions"
								description="Automated tips to improve the quality of your AI responses"
							>
								<MarkdownRenderer>
									{aiFeedback?.documentation_suggestions || ''}
								</MarkdownRenderer>
								<MarkdownRenderer>
									{aiFeedback?.blind_spots || ''}
								</MarkdownRenderer>
							</SettingsBlock>
						)}
					</Box>
				</Box>
			)}
		</Stack>
	);
}
